import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR, formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Banknote, Lock, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  draft: "badge-pending-draft",
  approved: "badge-approved",
  paid: "badge-approved",
};

const monthLabel = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
};

const monthOptions = (() => {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 18; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
    out.push({ iso, label: d.toLocaleDateString("en-IN", { month: "long", year: "numeric" }) });
  }
  return out;
})();

export default function PayrollList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Rev. 2026-02 — Include hr_admin & hr_maker (maker-checker roles).
  const canEdit = ["super_admin", "hr", "hr_admin", "hr_maker"].includes(user?.role);

  const [runs, setRuns] = useState([]);
  const [units, setUnits] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ unit_id: "", month: monthOptions[0].iso, days_in_month: 30 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [r, u] = await Promise.all([api.get("/payroll/runs"), api.get("/units")]);
      setRuns(r.data);
      setUnits(u.data);
    } catch {
      toast.error("Failed to load");
    }
  };
  useEffect(() => { load(); }, []);

  const onCreate = async () => {
    if (!form.unit_id) return toast.error("Pick a unit");
    setBusy(true);
    try {
      const { data } = await api.post("/payroll/runs", form);
      toast.success("Run created");
      setOpen(false);
      navigate(`/payroll/runs/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this draft run?")) return;
    try {
      await api.delete(`/payroll/runs/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const unitName = (id) => units.find((u) => u.id === id)?.name || "—";

  return (
    <div data-testid="payroll-list-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Phase 3</div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Payroll
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Compute salaries, generate payslip PDFs, lock approved runs.
          </p>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-run-btn" className="brand-bg brand-bg-hover text-white">
                <Plus className="h-4 w-4 mr-2" /> New run
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "Chivo" }}>New payroll run</DialogTitle>
                <DialogDescription>
                  Pull the monthly attendance summary into a draft payroll run.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Unit</Label>
                  <Select value={form.unit_id || undefined} onValueChange={(v) => setForm((f) => ({ ...f, unit_id: v }))}>
                    <SelectTrigger data-testid="run-unit"><SelectValue placeholder="Select unit" /></SelectTrigger>
                    <SelectContent>
                      {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Month</Label>
                  <Select value={form.month} onValueChange={(v) => setForm((f) => ({ ...f, month: v }))}>
                    <SelectTrigger data-testid="run-month"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {monthOptions.map((m) => <SelectItem key={m.iso} value={m.iso}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Days in month</Label>
                  <Input
                    type="number"
                    min="28" max="31"
                    data-testid="run-days"
                    value={form.days_in_month}
                    onChange={(e) => setForm((f) => ({ ...f, days_in_month: Number(e.target.value) }))}
                  />
                  <p className="text-[11px] text-slate-500 mt-1">Bravura standard divisor = 30. Override if needed.</p>
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="run-submit-btn" onClick={onCreate} disabled={busy}
                        className="brand-bg brand-bg-hover text-white">
                  {busy ? "Creating..." : "Create draft"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="runs-table">
          <thead className="bg-slate-50 border-b">
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Month</th>
              <th className="px-4 py-3">Unit</th>
              <th className="px-4 py-3 text-right">Employees</th>
              <th className="px-4 py-3 text-right">Gross</th>
              <th className="px-4 py-3 text-right">Net</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3 w-12"></th>
              <th className="px-4 py-3 w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {runs.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-500">No runs yet.</td></tr>
            )}
            {runs.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-900">
                  <Banknote className="h-3.5 w-3.5 text-slate-400 inline mr-2" />
                  {monthLabel(r.month)}
                </td>
                <td className="px-4 py-3 text-slate-700">{r.unit_name || unitName(r.unit_id)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{r.employee_count}</td>
                <td className="px-4 py-3 text-right tabular-nums">{formatINR(r.total_gross || 0)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-semibold brand-text">{formatINR(r.total_net || 0)}</td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className={`${STATUS_COLOR[r.status]} border uppercase text-[10px]`}>
                    {r.status === "approved" && <Lock className="h-3 w-3 mr-1" />}
                    {r.status}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{formatDateTime(r.created_at)}</td>
                <td className="px-4 py-3">
                  <button
                    data-testid={`open-run-${r.id}`}
                    onClick={() => navigate(`/payroll/runs/${r.id}`)}
                    className="p-1 hover:bg-slate-100 rounded"
                  >
                    <FileText className="h-4 w-4 text-slate-500" />
                  </button>
                </td>
                <td className="px-4 py-3">
                  {canEdit && r.status === "draft" && (
                    <button onClick={() => onDelete(r.id)} className="p-1 hover:bg-red-50 rounded text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
